import { GraphId } from "components/Graph/types.ts";
import { useUser } from "hooks/useUser.ts";

const LOCAL_STORAGE_KEY = "graph-hidden-lines";

interface HiddenLinesLocalStorage {
    [userId: string]: {
        [graphId: string]: string[];
    };
}

export const useHiddenLinesLocalStorage = () => {
    const { id } = useUser().getAuthenticatedUser() || {};

    const get = (graphId: GraphId): string[] => {
        const storageObject = localStorage.getItem(LOCAL_STORAGE_KEY);

        if (!storageObject) {
            return [];
        }

        try {
            const parsed = JSON.parse(storageObject) as HiddenLinesLocalStorage;
            return parsed?.[id ?? ""]?.[graphId] ?? [];
        } catch {
            return [];
        }
    };

    const set = (hiddenLines: string[], graphId: GraphId) => {
        if (!id) {
            return;
        }

        const storageObject = localStorage.getItem(LOCAL_STORAGE_KEY);
        let parsed: HiddenLinesLocalStorage = {};

        if (storageObject) {
            try {
                parsed = JSON.parse(storageObject) as HiddenLinesLocalStorage;
            } catch {
                // Reset corrupted storage
            }
        }

        const userGraphs = parsed[id] || {};
        const updatedUserGraphs = { ...userGraphs, [graphId]: hiddenLines };
        const updatedStorageObject = { ...parsed, [id]: updatedUserGraphs };

        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedStorageObject));
        } catch (error) {
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                console.warn("LocalStorage quota exceeded, clearing hidden lines");
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        }
    };

    return { get, set };
};